import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useCallback, useState } from "react";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { subscriptionModule } from "@/modules/subscriptions";
import { api } from "@/modules/api";
import { DateTime } from "luxon";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import {
  buildPaywallEventPayload,
  buildMonetizationUsagePayload,
  buildProactivePaywallEventPayload,
} from "@/domains/metrics/monetization";
import { useLocation } from "react-router-dom";
import { startProactiveCheckout } from "@/components/modal/proactive-upsell/proactive-checkout";
import {
  SettingsSubscriptionManagementPageView,
  SettingsSubscriptionPlan,
} from "@/pages/settings/settings-subscription-management-page.view";
import type { BillingSubscriptionObservable } from "@/store/subscriptions/BillingSubscriptionObservable";
import type { FeatureAccessObservable } from "@/store/subscriptions/FeatureAccessObservable";
import { PROACTIVE_PRICING_CONTROL_ASSIGNMENT } from "@/store/subscriptions";
import {
  CommercialPlanKey,
  getCompatibleCommercialCatalog,
  isCommercialRolloutActive,
  type CommercialPlanChangePreview,
  type CommercialPlanChangeResult,
  type CommercialPlanKey as CommercialPlanKeyType,
} from "@/modules/commercial-plans";
import {
  applyCommercialPlanChange,
  previewCommercialPlanChange,
} from "@/modules/commercial-plans/plan-change";

const getSettingsSubscriptionPlan = ({
  billingSubscription,
  featureAccess,
}: {
  billingSubscription: BillingSubscriptionObservable | undefined;
  featureAccess: FeatureAccessObservable | undefined;
}): SettingsSubscriptionPlan => {
  if (featureAccess?.isPaid === true) {
    if (featureAccess.isProactive) {
      return SettingsSubscriptionPlan.Proactive;
    }

    return SettingsSubscriptionPlan.Pro;
  }

  if (!billingSubscription?.isPaid || billingSubscription.hasExpiredEnd) {
    return SettingsSubscriptionPlan.Free;
  }

  if (billingSubscription.isProactive) {
    return SettingsSubscriptionPlan.Proactive;
  }

  return SettingsSubscriptionPlan.Pro;
};

const shouldUseAppleProactiveControlPrice = ({
  billingSubscription,
  plan,
}: {
  billingSubscription: BillingSubscriptionObservable | undefined;
  plan: SettingsSubscriptionPlan;
}): boolean =>
  plan === SettingsSubscriptionPlan.Proactive && billingSubscription?.provider === "APPLE";

const getPlanDisplayName = (plan: SettingsSubscriptionPlan): string => {
  if (plan === SettingsSubscriptionPlan.Proactive) {
    return "Proactive";
  }

  if (plan === SettingsSubscriptionPlan.Pro) {
    return "Pro";
  }

  return "Free";
};

const getGrantedAccessStatusText = (plan: SettingsSubscriptionPlan): string | undefined => {
  if (plan === SettingsSubscriptionPlan.Free) {
    return undefined;
  }

  return `${getPlanDisplayName(plan)} access is provided by Mem.`;
};

const getDowngradeCopy = ({
  subscriptionEndsAt,
  plan,
}: {
  subscriptionEndsAt: string | null | undefined;
  plan: SettingsSubscriptionPlan;
}): { downgradeBadgeLabel: string; downgradePreposition: string; planEndsText: string } => {
  const planName = getPlanDisplayName(plan);

  if (!subscriptionEndsAt) {
    return {
      downgradeBadgeLabel: "Downgrading soon",
      downgradePreposition: "soon",
      planEndsText: `Your ${planName} plan ends soon.`,
    };
  }

  const now = DateTime.local().startOf("day");
  const periodEnd = DateTime.fromISO(subscriptionEndsAt);
  if (!periodEnd.isValid) {
    return {
      downgradeBadgeLabel: "Downgrading soon",
      downgradePreposition: "soon",
      planEndsText: `Your ${planName} plan ends soon.`,
    };
  }

  const daysUntilPeriodEnd = Math.round(periodEnd.diff(now, "days").days);
  const downgradePreposition = daysUntilPeriodEnd > 0 ? `in ${daysUntilPeriodEnd} days` : "soon";

  return {
    downgradeBadgeLabel: `Downgrading ${downgradePreposition}`,
    downgradePreposition,
    planEndsText: `Your ${planName} plan ends on ${periodEnd.toFormat("LLLL d, yyyy")}.`,
  };
};

export const SettingsSubscriptionManagementPage = observer(() => {
  const { store, pageStore } = useAppStore();
  const settingsSubscriptionManagementPage = pageStore.settingsSubscriptionManagementPage;
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [subscribingPlan, setSubscribingPlan] = useState<SettingsSubscriptionPlan | null>(null);
  const [changingCommercialPlanKey, setChangingCommercialPlanKey] =
    useState<CommercialPlanKeyType | null>(null);
  const isSubscribing = subscribingPlan !== null;
  const nextMonthName = DateTime.local().plus({ months: 1 }).toFormat("LLLL");
  const renewalDateText = `${nextMonthName} 1st`;

  useEffectOnMount(() => {
    (async () => {
      setIsLoading(true);

      try {
        await Promise.all([
          api.get("/v2/me/paywall/configuration"),
          store.platformInfo.fetchPlatformInfo(),
        ]);

        const loadedPlan = getSettingsSubscriptionPlan({
          billingSubscription: store.subscriptions.billingSubscription,
          featureAccess: store.subscriptions.featureAccess,
        });
        const commercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
        const commercialRolloutIsActive = isCommercialRolloutActive({
          catalog: store.platformInfo.commercialCatalog,
          snapshot: commercialPlanSnapshot,
        });
        if (commercialRolloutIsActive) {
          trackEvent(
            TrackedEvent.PaywallView,
            buildPaywallEventPayload({ store, context: "settings" })
          );
        }
        const shouldLoadProactivePricing =
          !commercialRolloutIsActive &&
          (loadedPlan === SettingsSubscriptionPlan.Free ||
            loadedPlan === SettingsSubscriptionPlan.Pro ||
            (loadedPlan === SettingsSubscriptionPlan.Proactive &&
              !store.subscriptions.effectivePlanUsesGrantedAccess &&
              !shouldUseAppleProactiveControlPrice({
                billingSubscription: store.subscriptions.billingSubscription,
                plan: loadedPlan,
              })));

        if (shouldLoadProactivePricing) {
          await store.subscriptions.ensureProactivePricingAssignment();
        }
      } finally {
        setIsLoading(false);
      }
    })();
  });

  const billingSubscription = store.subscriptions.billingSubscription;
  const featureAccess = store.subscriptions.featureAccess;
  const plan = getSettingsSubscriptionPlan({
    billingSubscription,
    featureAccess,
  });
  const effectivePlanUsesGrantedAccess = store.subscriptions.effectivePlanUsesGrantedAccess;
  const isProactiveTrialEligible = store.subscriptions.isProactiveUpsellTrialEligible;
  const useAppleProactiveControlPrice = shouldUseAppleProactiveControlPrice({
    billingSubscription,
    plan,
  });
  const proactivePriceText = useAppleProactiveControlPrice
    ? PROACTIVE_PRICING_CONTROL_ASSIGNMENT.display_price
    : (store.subscriptions.proactivePricingAssignment?.display_price ??
      PROACTIVE_PRICING_CONTROL_ASSIGNMENT.display_price);
  const grantedAccessStatusText = effectivePlanUsesGrantedAccess
    ? getGrantedAccessStatusText(plan)
    : undefined;
  const downgradeCopy =
    !effectivePlanUsesGrantedAccess &&
    billingSubscription?.isScheduledToEnd &&
    plan !== SettingsSubscriptionPlan.Free
      ? getDowngradeCopy({
          subscriptionEndsAt: billingSubscription.effectiveSubscriptionEndsAt,
          plan,
        })
      : undefined;
  const canManagePlan = billingSubscription?.isActivePaid === true;
  const rawCommercialPlanSnapshot = store.platformInfo.commercialPlanSnapshot;
  const rawCommercialCatalog = getCompatibleCommercialCatalog({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: rawCommercialPlanSnapshot,
  });
  const commercialRolloutIsActive = isCommercialRolloutActive({
    catalog: store.platformInfo.commercialCatalog,
    snapshot: rawCommercialPlanSnapshot,
  });
  const commercialPlanSnapshot = commercialRolloutIsActive ? rawCommercialPlanSnapshot : null;
  const commercialCatalog = commercialRolloutIsActive ? rawCommercialCatalog : null;
  const canStartCommercialSubscription =
    commercialCatalog !== null &&
    commercialPlanSnapshot?.currentPlan.isLegacy === false &&
    commercialPlanSnapshot.currentPlan.key === CommercialPlanKey.Mem &&
    !subscriptionModule.isAppleProvider(billingSubscription?.provider ?? null);
  const canChangeCommercialPlan =
    commercialCatalog !== null &&
    commercialPlanSnapshot !== null &&
    commercialPlanSnapshot !== undefined &&
    commercialPlanSnapshot.currentPlan.key !== CommercialPlanKey.Mem &&
    (!commercialPlanSnapshot.currentPlan.isLegacy ||
      commercialPlanSnapshot.migration?.eligible === true) &&
    billingSubscription?.provider === "STRIPE" &&
    billingSubscription.isActivePaid;

  const handleSubscribe = useCallback(
    async (billingStrategy: BillingStrategyEnum) => {
      if (isSubscribing) return;
      setSubscribingPlan(SettingsSubscriptionPlan.Pro);
      try {
        const monetizationProperties = await buildMonetizationUsagePayload(store);
        trackEvent(TrackedEvent.PaywallUpgradeClick, {
          context: "settings",
          ...monetizationProperties,
        });

        await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy,
          location,
          store,
        });
      } finally {
        setSubscribingPlan(null);
      }
    },
    [isSubscribing, location, store]
  );

  const handleSubscribeToProactive = useCallback(async () => {
    if (isSubscribing) return;

    if (store.subscriptions.shouldUseAppleProactiveUpgradeModal) {
      store.subscriptions.openProactiveUpsell();
      return;
    }

    setSubscribingPlan(SettingsSubscriptionPlan.Proactive);
    try {
      trackEvent(TrackedEvent.PaywallUpgradeClick, buildProactivePaywallEventPayload({ store }));

      await startProactiveCheckout({
        location,
        store,
      });
    } finally {
      setSubscribingPlan(null);
    }
  }, [isSubscribing, location, store]);

  const handleManagePlan = useCallback(async () => {
    const currentProvider = billingSubscription?.provider ?? null;

    if (subscriptionModule.isAppleProvider(currentProvider)) {
      settingsSubscriptionManagementPage.openAppleManagedDialog();
      return;
    }

    await subscriptionModule.getBillingPortalAndNavigate({ returnUrl: location });
  }, [billingSubscription?.provider, location, settingsSubscriptionManagementPage]);

  const handleStartCommercialSubscription = useCallback(
    async ({
      paidExtraUsageBlocks,
      planKey: commercialPlanKey,
    }: {
      paidExtraUsageBlocks: number;
      planKey: CommercialPlanKeyType;
    }) => {
      if (!canStartCommercialSubscription || changingCommercialPlanKey !== null) {
        return;
      }

      if (commercialPlanKey === CommercialPlanKey.Mem) {
        return;
      }

      const selectedCommercialPlan = commercialCatalog?.plans.find(
        candidate => candidate.key === commercialPlanKey
      );
      if (!selectedCommercialPlan?.checkoutAvailable) {
        return;
      }

      setChangingCommercialPlanKey(commercialPlanKey);
      try {
        trackEvent(
          TrackedEvent.PaywallUpgradeClick,
          buildPaywallEventPayload({
            store,
            context: "settings",
            targetCommercialPlanKey: commercialPlanKey,
            commercialUpgradeAction: "select_plan",
          })
        );

        await subscriptionModule.createCheckoutSessionAndNavigate({
          billingStrategy: BillingStrategyEnum.MONTHLY,
          commercialPlanKey,
          extraUsageBlocks: paidExtraUsageBlocks,
          location,
          store,
        });
      } finally {
        setChangingCommercialPlanKey(null);
      }
    },
    [canStartCommercialSubscription, changingCommercialPlanKey, commercialCatalog, location, store]
  );

  const handlePreviewCommercialPlanChange = useCallback(
    async ({
      paidExtraUsageBlocks,
      planKey,
    }: {
      paidExtraUsageBlocks: number;
      planKey: CommercialPlanKeyType;
    }): Promise<CommercialPlanChangePreview> => {
      if (!canChangeCommercialPlan || !commercialCatalog || !commercialPlanSnapshot) {
        throw new Error("Plan changes are unavailable for this subscription.");
      }

      return previewCommercialPlanChange({
        catalog: commercialCatalog,
        currentPaidExtraUsageBlocks: commercialPlanSnapshot.blockPolicy?.paidBlocks ?? 0,
        currentPlanKey: commercialPlanSnapshot.currentPlan.key,
        isLegacyMigration: commercialPlanSnapshot.currentPlan.isLegacy,
        targetPaidExtraUsageBlocks: paidExtraUsageBlocks,
        targetPlanKey: planKey,
      });
    },
    [canChangeCommercialPlan, commercialCatalog, commercialPlanSnapshot]
  );

  const handleApplyCommercialPlanChange = useCallback(
    async ({
      idempotencyKey,
      preview,
    }: {
      idempotencyKey: string;
      preview: CommercialPlanChangePreview;
    }): Promise<CommercialPlanChangeResult> => {
      if (
        !canChangeCommercialPlan ||
        !commercialCatalog ||
        !commercialPlanSnapshot ||
        changingCommercialPlanKey !== null
      ) {
        throw new Error("Plan changes are unavailable for this subscription.");
      }

      setChangingCommercialPlanKey(preview.targetPlanKey);
      try {
        const result = await applyCommercialPlanChange({
          catalog: commercialCatalog,
          currentPaidExtraUsageBlocks: commercialPlanSnapshot.blockPolicy?.paidBlocks ?? 0,
          currentPlanKey: commercialPlanSnapshot.currentPlan.key,
          idempotencyKey,
          isLegacyMigration: commercialPlanSnapshot.currentPlan.isLegacy,
          preview,
        });
        await store.platformInfo
          .fetchPlatformInfo({ reconcileStripeSubscription: true })
          .catch(() => undefined);
        return result;
      } finally {
        setChangingCommercialPlanKey(null);
      }
    },
    [
      canChangeCommercialPlan,
      changingCommercialPlanKey,
      commercialCatalog,
      commercialPlanSnapshot,
      store.platformInfo,
    ]
  );

  return (
    <SettingsSubscriptionManagementPageView
      isLoading={isLoading}
      plan={plan}
      renewalDateText={renewalDateText}
      downgradeBadgeLabel={downgradeCopy?.downgradeBadgeLabel}
      downgradePreposition={downgradeCopy?.downgradePreposition}
      planEndsText={downgradeCopy?.planEndsText}
      grantedAccessStatusText={grantedAccessStatusText}
      effectivePlanUsesGrantedAccess={effectivePlanUsesGrantedAccess}
      isProactiveTrialEligible={isProactiveTrialEligible}
      proactivePriceText={proactivePriceText}
      canManagePlan={canManagePlan}
      subscribingPlan={subscribingPlan}
      commercialPlanSnapshot={commercialPlanSnapshot}
      commercialCatalog={commercialCatalog}
      changingCommercialPlanKey={changingCommercialPlanKey}
      initialCommercialPlanKey={settingsSubscriptionManagementPage.selectedCommercialPlanKey}
      onSubscribe={handleSubscribe}
      onSubscribeToProactive={handleSubscribeToProactive}
      onManagePlan={handleManagePlan}
      onApplyCommercialPlanChange={
        canChangeCommercialPlan ? handleApplyCommercialPlanChange : undefined
      }
      onPreviewCommercialPlanChange={
        canChangeCommercialPlan ? handlePreviewCommercialPlanChange : undefined
      }
      onStartCommercialSubscription={
        canStartCommercialSubscription ? handleStartCommercialSubscription : undefined
      }
    />
  );
});
